import { API } from "."
import { makeParams } from "@/utils/helper"
import {
  CreatePlayerGroupPayload,
  CreateSeasonWeekPayload,
  ReadCalculateUnitParams,
  ReadCalculateUnitResponse,
  ReadActionsResponse,
  ReadGroupPlayersResponse,
  ReadMetricGroupResponse,
  ReadMetricGroupsResponse,
  ReadPlayerGroupsResponse,
  ReadPlayersResponse,
  ReadSeasonWeekResponse,
  ReadSettingAdvancedResponse,
  ReadUnitResponse,
  ReadUnitsResponse,
  UpsertActionsPayload,
  UpsertMetricGroupPayload,
  UpsertMetricGroupResponse,
  UpsertPlayerGroupPayload,
  UpsertSettingAdvancedPayload,
  UpsertSettingAdvancedResponse,
  UpsertUnitPayload,
  UpsertUnitResponse,
  ReadJumpActionResponse,
  ReadDiveActionResponse,
  ReadCODActionResponse,
  UpsertJumpActionPayload,
  UpsertDiveActionPayload,
  UpsertCODActionPayload,
  CalculatedMetricResponse,
  CalculatedMetricPayload,
} from "@/types/setting"

/* 호출 시 /teams/{idfTeam} */

/* Unit */
/* 지표별 단위 옵션 목록 조회 */
export async function readUnits() {
  return await API.get<ReadUnitsResponse>("/settings/units")
}

/* 팀 단위 설정 정보 조회 */
export async function readUnit() {
  return await API.get<ReadUnitResponse>("/settings/unit")
}

/* 팀 단위 설정 정보 수정 */
export async function upsertUnit(payload: UpsertUnitPayload) {
  return await API.put<UpsertUnitResponse>("/settings/unit", payload)
}

/* 단위 변환 계산 */
export async function readCalculateUnit({ type, value }: ReadCalculateUnitParams) {
  const params = makeParams({ type, value })

  return await API.get<ReadCalculateUnitResponse>("/settings/calc/unit", { params })
}

/* Metric Group */
/* 메트릭 그룹 옵션 목록 조회 */
export async function readMetricGroups() {
  return await API.get<ReadMetricGroupsResponse>("/metric-group") // /teams/{idfTeam} 제외
}

/* 메트릭 그룹 설정 조회 */
export async function readMetricGroup() {
  return await API.get<ReadMetricGroupResponse>("/settings/metric-group")
}

/* 메트릭 그룹 설정 수정 */
export async function upsertMetricGroup(payload: UpsertMetricGroupPayload) {
  return await API.put<UpsertMetricGroupResponse>("/settings/metric-group", payload)
}

/* Team Player Group */
/* 팀 선수 그룹 목록 조회 */
export async function readPlayerGroups() {
  return await API.get<ReadPlayerGroupsResponse>("/settings/groups")
}

/* 팀 선수 그룹 추가 */
export async function createPlayerGroup(payload: CreatePlayerGroupPayload) {
  return await API.post<void>("/settings/groups", payload)
}

/* 팀 선수 그룹 이름 수정 */
export async function upsertPlayerGroup(payload: UpsertPlayerGroupPayload) {
  const { idfTeamPlayerGroup } = payload

  return await API.put<void>(`/settings/groups/${idfTeamPlayerGroup}`, payload)
}

/* 팀 선수 그룹 제거 */
export async function deletePlayerGroup(idfTeamPlayerGroup: number) {
  return await API.delete<void>(`/settings/groups/${idfTeamPlayerGroup}`)
}

/* 팀 선수 목록 조회 */
export async function readPlayers() {
  return await API.get<ReadPlayersResponse>("/settings/players")
}
/* 현재 선택된 팀이 아닌 다른 팀 선수 목록 조회 */
export async function readOtherPlayers(idfTeam: number) {
  return await API.get<ReadPlayersResponse>(`/teams/${idfTeam}/settings/players`)
}
/* 그룹 선수 목록 조회 */
export async function readGroupPlayers(idfTeamPlayerGroup: number) {
  return await API.get<ReadGroupPlayersResponse>(`/settings/groups/${idfTeamPlayerGroup}/players`)
}
/* Advanced */
/* ACWR의 급성, 만성 설정 기간 조회 */
export async function readACWR() {
  return await API.get<ReadSettingAdvancedResponse>("/settings/advanced/acwr")
}

/* ACWR의 급성, 만성 설정 기간 수정 */
export async function upsertACWR(payload: UpsertSettingAdvancedPayload) {
  return await API.put<UpsertSettingAdvancedResponse>("/settings/advanced/acwr", payload)
}

/* Season week 조회 */
export async function readSeasonWeek() {
  return await API.get<ReadSeasonWeekResponse>("/settings/advanced/season-week")
}

/* Season week 설정 */
export async function createSeasonWeek(payload: CreateSeasonWeekPayload) {
  return await API.post<void>("/settings/advanced/season-week", payload)
}

/* Season week 일괄 매칭 */
export async function matchSeasonWeekActivities(idfSeasonWeek: number) {
  return await API.post<void>(`/settings/advanced/season-week/${idfSeasonWeek}/match-activities`)
}

/* Action */
/* 지표별 액션 임계치 전역 설정 조회 */
export async function readRunAction() {
  return await API.get<ReadActionsResponse>("/settings/actions/run")
}
export async function readAccelAction() {
  return await API.get<ReadActionsResponse>("/settings/actions/accel")
}
export async function readDecelAction() {
  return await API.get<ReadActionsResponse>("/settings/actions/decel")
}
export async function readJumpAction() {
  return await API.get<ReadJumpActionResponse>("/settings/actions/jump")
}
export async function readDiveAction() {
  return await API.get<ReadDiveActionResponse>("/settings/actions/dive")
}
export async function readCODAction() {
  return await API.get<ReadCODActionResponse>("/settings/actions/cod")
}

/* 지표별 임계치 설정 수정 */
export async function upsertRunAction(payload: UpsertActionsPayload) {
  return await API.put<void>("/settings/actions/run", payload)
}
export async function upsertAccelAction(payload: UpsertActionsPayload) {
  return await API.put<void>("/settings/actions/accel", payload)
}
export async function upsertDecelAction(payload: UpsertActionsPayload) {
  return await API.put<void>("/settings/actions/decel", payload)
}
export async function upsertJumpAction(payload: UpsertJumpActionPayload) {
  return await API.put<void>("/settings/actions/jump", payload)
}
export async function upsertDiveAction(payload: UpsertDiveActionPayload) {
  return await API.put<void>("/settings/actions/dive", payload)
}
export async function upsertCODAction(payload: UpsertCODActionPayload) {
  return await API.put<void>("/settings/actions/cod", payload)
}

/* Custom metric calculator */
export async function readCalculatedMetrics() {
  return await API.get<CalculatedMetricResponse[]>("/settings/metrics/calc")
}

export async function createCalculatedMetrics(payload: CalculatedMetricPayload) {
  return await API.post<CalculatedMetricResponse>("/settings/metrics/calc", payload)
}

export async function updateCalculatedMetrics(payload: CalculatedMetricPayload) {
  const { idfMetric } = payload
  return await API.put<CalculatedMetricResponse>(`/settings/metrics/calc/${idfMetric}`, payload)
}

export async function deleteCalculatedMetrics(idfMetric: number) {
  return await API.delete(`/settings/metrics/calc/${idfMetric}`)
}
